import * as React from "react";
import Grid from "@mui/material/Grid";
import classes from "./EmploymentInformation.module.css";
import TextField from "@mui/material/TextField";
import AddSupervisorModal from "../AddSupervisorModal/AddSupervisorModal";
import {
  getAllCenterLocations,
  getAllEmploymentTypes,
  getAllEmploymentStatus,
} from "services/centerPointUserServices";
import Autocomplete from "@mui/material/Autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { addNonHRMSEmployeeActions } from "Store/addNonHRMSEmployee";
import SupervisorSearch from "../SupervisorSearch/SupervisorSearch";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { format } from "date-fns";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Clear } from "@mui/icons-material";

const today = new Date();
const EmploymentInformation = (props) => {
  const { errorHandler } = props;
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [employmentTypeArray, setEmploymentTypeArray] = React.useState([]);
  const [centerLocationArray, setCenterLocationArray] = React.useState([]);
  const [inputType, setInputType] = React.useState("text");
  const [employmentStatusArray, setEmploymentStatusArray] = React.useState([]);
  const details = useSelector((state) => state.addNonHRMSEmployee);

  const handleFocus = () => {
    setInputType("date");
  };

  const handleBlur = () => {
    setInputType("text");
  };

  React.useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await fetchData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchData = async () => {
    dispatch(addNonHRMSEmployeeActions.updateEmploymentInformationLoader(true));
    try {
      const [
        centerLocationResponse,
        employmentTypeResponse,
        employmentStatusResponse,
      ] = await Promise.all([
        getAllCenterLocations(),
        getAllEmploymentTypes(),
        getAllEmploymentStatus(),
      ]);

      if (centerLocationResponse?.data?.message === "Success") {
        setCenterLocationArray(centerLocationResponse?.data?.data);
      } else {
        errorHandler(true, "Center Locations could not be fetched.", "error");
      }

      if (employmentTypeResponse?.data?.message === "Success") {
        setEmploymentTypeArray(employmentTypeResponse?.data?.data);
      } else {
        errorHandler(true, "Employment types could not be fetched.", "error");
      }

      if (employmentStatusResponse?.data?.message === "Success") {
        setEmploymentStatusArray(employmentStatusResponse?.data?.data);
      } else {
        errorHandler(true, "Employment Status could not be fetched.", "error");
      }
    } catch (error) {
      errorHandler(true, "Some internal error occurred.", "error");
    }
    dispatch(
      addNonHRMSEmployeeActions.updateEmploymentInformationLoader(false),
    );
  };

  const onSupervisorChange = (supervisor) => {
    dispatch(addNonHRMSEmployeeActions.updateSupervisor(supervisor));
  };

  const handleClose = () => setOpen(false);
  return (
    <>
      <AddSupervisorModal open={open} handleClose={handleClose} />
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Autocomplete
            disablePortal
            blurOnSelect
            disabled={details.disableElements}
            value={
              details.employmentType === 0
                ? null
                : employmentTypeArray?.find(
                    (item) => item?.CP_JobTypeId === details.employmentType,
                  )
            }
            options={employmentTypeArray}
            onChange={(e, value) => {
              dispatch(
                addNonHRMSEmployeeActions.updateEmploymentType(
                  value?.CP_JobTypeId,
                ),
              );
            }}
            getOptionLabel={(option) =>
              option?.Name === "" ? "" : `${option?.Name}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type"
                placeholder="Type"
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            disablePortal
            disabled={details.disableElements}
            blurOnSelect
            options={centerLocationArray}
            value={details.centerLocation}
            className={classes.searchField}
            onChange={(e, value) =>
              dispatch(addNonHRMSEmployeeActions.updateCenterLocation(value))
            }
            getOptionLabel={(option) =>
              option?.LocationName === ""
                ? ""
                : `${option?.LocationName + " (" + option?.GeoLocCd + ")"}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Center Location"
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={3}>
          <Autocomplete
            disablePortal
            blurOnSelect
            disabled={details.disableElements}
            value={
              details.employmentStatus === 0
                ? null
                : employmentStatusArray?.find(
                    (item) =>
                      item?.CP_EmployeeStatusId === details.employmentStatus,
                  )
            }
            options={employmentStatusArray}
            onChange={(e, value) => {
              dispatch(
                addNonHRMSEmployeeActions.updateEmploymentStatus(
                  value?.CP_EmployeeStatusId,
                ),
              );
            }}
            getOptionLabel={(option) =>
              option?.Name === "" ? "" : `${option?.Name}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Status"
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Status Date"
              value={
                details.statusDate === "" ? null : moment(details.statusDate)
              }
              disabled={details.disableElements}
              onChange={(newValue) => {
                if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                  dispatch(
                    addNonHRMSEmployeeActions.updateStatusDate(
                      format(newValue, "yyyy-MM-dd"),
                    ),
                  );
                }
              }}
              format="yyyy-MM-dd"
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={false}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {details.statusDate && (
                          <Tooltip title={"Clear"}>
                            <IconButton
                              onClick={() =>
                                dispatch(
                                  addNonHRMSEmployeeActions.updateStatusDate(
                                    "",
                                  ),
                                )
                              }
                              edge="end"
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3} className={classes.display}>
          <SupervisorSearch
            onChange={(supervisor) => onSupervisorChange(supervisor)}
            onReset={(supervisor) => onSupervisorChange(supervisor)}
            label="Supervisor *"
            disabled={details.disableElements}
            defaultSupervisorName={
              details.supervisor.FirstName
                ? details.supervisor.FirstName +
                  " " +
                  details.supervisor.LastName
                : ""
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="outlined-basic"
            variant="outlined"
            label="Comments"
            value={details.comments}
            disabled={details.disableElements}
            fullWidth
            onChange={(e) => {
              dispatch(
                addNonHRMSEmployeeActions.updateComments(e.target.value),
              );
            }}
            multiline
            rows={3}
            inputProps={{ maxLength: 4000 }}
            helperText={
              <Box
                component={"span"}
                sx={{
                  color: details?.comments?.length === 4000 ? "red" : "inherit",
                }}
              >{`${details.comments ? details.comments.length : 0}/4000`}</Box>
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default EmploymentInformation;
